import { ref, type Ref } from 'vue'

import type { ReportExportOutcome } from '../models/export.types'
import { shareFailureKey } from '../service/sheetShare'

import type { UseReportExport } from './useReportExport'

import { ApiError } from '@/shared/http'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui'

/** The optional "share with" step before a Sheets export (ORP-25238). */
export interface SheetSharePicker {
  open: Ref<boolean>
  /** The workspace's sharing list — the only addresses the picker offers. */
  options: Ref<string[]>
  confirm: (shareWith: readonly string[]) => Promise<void>
  /** Close without exporting. */
  dismiss: () => void
}

export interface UseDataVisibleExportActions {
  exportSheet: () => Promise<void>
  downloadExcel: () => Promise<void>
  sharePicker: SheetSharePicker
}

function openSafeUrl(value: string): boolean {
  try {
    const url = new URL(value, window.location.origin)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false
    window.open(url.toString(), '_blank', 'noopener,noreferrer')
    return true
  } catch {
    return false
  }
}

/** Add user feedback and safe URL handling to the transport-only export flow. */
export function useDataVisibleExportActions(
  exportFlow: UseReportExport,
): UseDataVisibleExportActions {
  const { t } = useI18n()
  const toast = useToast()
  const pickerOpen = ref(false)
  const pickerOptions = ref<string[]>([])

  function settleExportToast(id: number, succeeded: boolean, message?: string): void {
    toast.update(id, {
      message:
        message ?? t(succeeded ? 'reports.actions.exportSuccess' : 'reports.actions.exportError'),
      tone: succeeded ? 'success' : 'danger',
      progress: false,
      dismissible: true,
      timeout: 4000,
    })
  }

  /**
   * Who the sheet reached. A refused address does not fail the export — the sheet exists — so it is
   * a separate warning that names each address and Drive's reason.
   */
  function reportSharing(result: ReportExportOutcome): void {
    if (result.kind !== 'sheet') return
    if (result.sharedWith.length > 0) {
      toast.info(t('reports.sheetShare.sharedWith', { emails: result.sharedWith.join(', ') }))
    }
    if (result.shareFailures.length > 0) {
      const lines = result.shareFailures.map((failure) =>
        t(
          failure.retryable ? 'reports.sheetShare.failureRetryable' : 'reports.sheetShare.failure',
          {
            email: failure.email,
            reason: t(`reports.sheetShare.reason.${shareFailureKey(failure.reason)}`),
          },
        ),
      )
      toast.warning(lines.join('\n'), { title: t('reports.sheetShare.failuresTitle'), timeout: 0 })
    }
  }

  /** A refused share (e.g. 422 `SHEET_SHARE_NOT_ALLOWED`) answers with a sentence; show it as-is. */
  function refusal(): string | undefined {
    const cause = exportFlow.error.value
    return ApiError.isApiError(cause) && cause.status !== null && cause.detail
      ? cause.detail
      : undefined
  }

  async function runSheet(shareWith: readonly string[]): Promise<void> {
    const toastId = toast.loader(t('reports.actions.exportPreparing'))
    const result = await exportFlow.exportSheet(undefined, shareWith)
    if (result?.kind !== 'sheet' || !openSafeUrl(result.url)) {
      settleExportToast(toastId, false, shareWith.length > 0 ? refusal() : undefined)
      return
    }
    settleExportToast(toastId, true)
    reportSharing(result)
  }

  /**
   * Offer the picker when the workspace's sharing list has addresses; otherwise export straight
   * away, as before. The sheet is always shared with the exporter themself.
   */
  async function exportSheet(): Promise<void> {
    const options = await exportFlow.loadShareOptions()
    if (options.length === 0) {
      await runSheet([])
      return
    }
    pickerOptions.value = options
    pickerOpen.value = true
  }

  async function confirm(shareWith: readonly string[]): Promise<void> {
    pickerOpen.value = false
    await runSheet(shareWith)
  }

  /**
   * An `async` outcome is a queued controller job delivered by e-mail, not a finished download, so
   * it must not settle as success. v3 called the same echoed `export_format` a completed download
   * (`OrphexReportTable.vue:1019-1023,973`); the only completion v4 claims is one it can prove — a
   * sheet URL that opened, or XLSX bytes written to disk.
   */
  function settleQueuedToast(id: number, recipients: readonly string[]): void {
    toast.update(id, {
      message: t('reports.actions.exportQueued', { recipients: recipients.join(', ') }),
      tone: 'info',
      progress: false,
      dismissible: true,
      timeout: 8000,
    })
  }

  async function downloadExcel(): Promise<void> {
    const toastId = toast.loader(t('reports.actions.exportPreparing'))
    const result = await exportFlow.downloadXlsx()
    if (result?.kind === 'async') {
      settleQueuedToast(toastId, result.recipients)
      return
    }
    settleExportToast(toastId, Boolean(result))
  }

  return {
    exportSheet,
    downloadExcel,
    sharePicker: {
      open: pickerOpen,
      options: pickerOptions,
      confirm,
      dismiss: () => {
        pickerOpen.value = false
      },
    },
  }
}
